/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import ActorAvatar from './ActorAvatar';
import LandStackButton from './LandStackButton';
import PullRequestStack from './PullRequestStack';
import PullRequestStateLabel from './PullRequestStateLabel';
import PullRequestVersions from './PullRequestVersions';
import TrustedRenderedMarkdown from './TrustedRenderedMarkdown';
import {gitHubPullRequestAtom} from './jotai';
import timeAgo from './timeAgo';
import {Box, Link, Text} from '@primer/react';
import {useAtomValue} from 'jotai';
import {Suspense} from 'react';

type Props = {
  height: number;
};

export default function PullRequestHeader({height}: Props): React.ReactElement | null {
  const pullRequest = useAtomValue(gitHubPullRequestAtom);

  if (pullRequest == null) {
    return null;
  }

  const {author, createdAt, isDraft, number, reviewDecision, state, titleHTML, url, lastComment} = pullRequest;
  const lastCommentBody = lastComment.nodes?.[0]?.body ?? null;

  return (
    <Box
      height={height}
      borderBottomWidth={1}
      borderBottomStyle="solid"
      borderBottomColor="border.default"
      display="flex"
      flexDirection="column"
      gridGap={2}
      padding={3}
      bg="canvas.subtle"
      sx={{position: 'relative'}}>
      <Box display="flex" alignItems="center" gridGap={2}>
        <Text
          sx={{
            fontWeight: 'bold',
            fontSize: '18px',
            color: '#2980b9',
            fontFamily: 'mono',
          }}>
          D{number}
        </Text>
        <Text sx={{fontWeight: 'bold', fontSize: '18px'}}>
          <TrustedRenderedMarkdown trustedHTML={titleHTML} inline={true} />
        </Text>
        <PullRequestStateLabel reviewDecision={reviewDecision ?? null} state={state} isDraft={isDraft} lastCommentBody={lastCommentBody} />
        <Link href={url} target="_blank">
          <Text fontSize={12} color="fg.muted">
            (GitHub)
          </Text>
        </Link>
      </Box>
      <Box display="flex" gridGap={2} alignItems="center">
        {author != null && (
          <Box display="flex" alignItems="center" gridGap={1}>
            <Box sx={{display: 'flex', lineHeight: 0, flexShrink: 0}}>
              <ActorAvatar login={author.login} url={author.avatarUrl} size={20} />
            </Box>
            <Text sx={{fontSize: 12, color: 'fg.muted', lineHeight: '20px'}}>
              {author.login} {createdAt != null && <>&middot; {timeAgo(createdAt)}</>}
            </Text>
          </Box>
        )}
        <PullRequestStack />
        <Suspense fallback={null}>
          <PullRequestVersions />
        </Suspense>
        <Box sx={{marginLeft: 'auto'}}>
          <LandStackButton />
        </Box>
      </Box>
    </Box>
  );
}
